
import { KeyboardMapping } from '@/types/keyboard';

// Define which finger is used for each key
export const keyboardMap: KeyboardMapping = {
  // QWERTY keyboard layout
  // Top row
  'q': { finger: 'pinky', hand: 'left', row: 'top' },
  'w': { finger: 'ring', hand: 'left', row: 'top' },
  'e': { finger: 'middle', hand: 'left', row: 'top' },
  'r': { finger: 'index', hand: 'left', row: 'top' },
  't': { finger: 'index', hand: 'left', row: 'top' },
  'y': { finger: 'index', hand: 'right', row: 'top' },
  'u': { finger: 'index', hand: 'right', row: 'top' },
  'i': { finger: 'middle', hand: 'right', row: 'top' },
  'o': { finger: 'ring', hand: 'right', row: 'top' },
  'p': { finger: 'pinky', hand: 'right', row: 'top' },
  
  // Home row
  'a': { finger: 'pinky', hand: 'left', row: 'home' },
  's': { finger: 'ring', hand: 'left', row: 'home' },
  'd': { finger: 'middle', hand: 'left', row: 'home' },
  'f': { finger: 'index', hand: 'left', row: 'home' },
  'g': { finger: 'index', hand: 'left', row: 'home' },
  'h': { finger: 'index', hand: 'right', row: 'home' },
  'j': { finger: 'index', hand: 'right', row: 'home' },
  'k': { finger: 'middle', hand: 'right', row: 'home' },
  'l': { finger: 'ring', hand: 'right', row: 'home' },
  ';': { finger: 'pinky', hand: 'right', row: 'home' },
  
  // Bottom row
  'z': { finger: 'pinky', hand: 'left', row: 'bottom' },
  'x': { finger: 'ring', hand: 'left', row: 'bottom' },
  'c': { finger: 'middle', hand: 'left', row: 'bottom' },
  'v': { finger: 'index', hand: 'left', row: 'bottom' },
  'b': { finger: 'index', hand: 'left', row: 'bottom' },
  'n': { finger: 'index', hand: 'right', row: 'bottom' },
  'm': { finger: 'index', hand: 'right', row: 'bottom' },
  ',': { finger: 'middle', hand: 'right', row: 'bottom' },
  '.': { finger: 'ring', hand: 'right', row: 'bottom' },
  '/': { finger: 'pinky', hand: 'right', row: 'bottom' },

  // Numbers and special characters
  '1': { finger: 'pinky', hand: 'left', row: 'top' },
  '2': { finger: 'ring', hand: 'left', row: 'top' },
  '3': { finger: 'middle', hand: 'left', row: 'top' },
  '4': { finger: 'index', hand: 'left', row: 'top' },
  '5': { finger: 'index', hand: 'left', row: 'top' },
  '6': { finger: 'index', hand: 'right', row: 'top' },
  '7': { finger: 'index', hand: 'right', row: 'top' },
  '8': { finger: 'middle', hand: 'right', row: 'top' },
  '9': { finger: 'ring', hand: 'right', row: 'top' },
  '0': { finger: 'pinky', hand: 'right', row: 'top' },
  '-': { finger: 'pinky', hand: 'right', row: 'top' },
  
  // Space
  ' ': { finger: 'thumb', hand: 'right', row: 'bottom' },
};

// Define common letter swaps that cause typing errors
export const commonSwaps = [
  ['a', 's'],
  ['s', 'd'],
  ['e', 'r'],
  ['r', 't'],
  ['f', 'g'],
  ['v', 'b'],
  ['n', 'm'],
  ['u', 'i'],
  ['i', 'o'],
  ['o', 'p'],
];

// Helper function to check if the domain contains common letter swaps
export function hasLetterSwitchingRisk(domain: string): number {
  let riskCount = 0;
  
  for (let i = 0; i < domain.length - 1; i++) {
    const currentPair = [domain[i], domain[i + 1]];
    const reversedPair = [domain[i + 1], domain[i]];
    
    // Check if this pair (reversed) is in our common swaps
    for (const swap of commonSwaps) {
      if ((currentPair[0] === swap[0] && currentPair[1] === swap[1]) || 
          (currentPair[0] === swap[1] && currentPair[1] === swap[0])) {
        riskCount++;
        break;
      }
    }
  }
  
  return riskCount;
}

// Helper to check if a consonant cluster exists
const consonants = 'bcdfghjklmnpqrstvwxyz';
export function hasConsonantClusters(domain: string): number {
  let clusterCount = 0;
  let consecutiveConsonants = 0;
  
  for (let i = 0; i < domain.length; i++) {
    if (consonants.includes(domain[i].toLowerCase())) {
      consecutiveConsonants++;
      if (consecutiveConsonants >= 3) {
        clusterCount++;
        consecutiveConsonants = 0; // Reset after finding a cluster
      }
    } else {
      consecutiveConsonants = 0;
    }
  }
  
  return clusterCount;
}

// Helper to count syllables (very approximate)
const vowels = 'aeiouy';
export function countSyllables(domain: string): number {
  // Remove non-letter characters and digits
  const cleanDomain = domain.toLowerCase().replace(/[^a-z]/g, '');
  
  if (cleanDomain.length === 0) return 0;
  
  // Count vowel sequences
  let syllableCount = 0;
  let isPreviousVowel = false;
  
  for (let i = 0; i < cleanDomain.length; i++) {
    const isCurrentVowel = vowels.includes(cleanDomain[i]);
    
    if (isCurrentVowel && !isPreviousVowel) {
      syllableCount++;
    }
    
    isPreviousVowel = isCurrentVowel;
  }
  
  // Ensure at least one syllable
  return Math.max(syllableCount, 1);
}

// Check if the domain is pronounceable
export function isPronounceableDomain(domain: string): boolean {
  // Clean domain to just letters
  const cleanDomain = domain.toLowerCase().replace(/[^a-z]/g, '');
  
  if (cleanDomain.length === 0) return false;
  
  // Simple heuristic: a pronounceable domain should have at least one vowel
  return cleanDomain.split('').some(char => vowels.includes(char));
}
